// Types
import { IPageParam } from "@/Common/api/types/pagination";

// APIs
import { getTitlings } from "@DataPages/api/financeManagement/titling/queries";

// Hooks
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";

// Helpers
import { and } from "@/Common/helpers/sonar";

// Constants
const PAGINATION_SIZE = 500;

export function getTitlingsQueryKey(args: { locationId: string; commissionCycleId: string; payPeriodId: string }) {
	const { locationId, commissionCycleId, payPeriodId } = args;
	return ["titling", locationId, commissionCycleId, payPeriodId];
}

export function useTitlingQuery(args: {
	enabled: boolean;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	paginationSize?: number;
	hasPermission: boolean;
}) {
	const { enabled, locationId, commissionCycleId, payPeriodId, paginationSize = PAGINATION_SIZE, hasPermission } = args;

	const {
		data: titlingData,
		isLoading,
		isFetching,
		hasNextPage,
		...otherResults
	} = usePaginatedResource({
		queryKey: getTitlingsQueryKey({ locationId, commissionCycleId, payPeriodId }),
		enabled: and(enabled, !!locationId, !!commissionCycleId, !!payPeriodId, hasPermission),
		initialPageParam: { after: "", first: paginationSize },
		getNextPageParam: buildGetNextPageParam(paginationSize),
		fetchAllPages: true,
		staleTime: Infinity,
		gcTime: Infinity,
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const paginatedResponse = await getTitlings({
				locationId,
				commissionCycleId,
				payPeriodId,
				first: typedPageParam.first,
				after: typedPageParam.after,
			});

			if (!paginatedResponse) {
				throw new Error("Error retrieving F&I Details data");
			}

			return paginatedResponse;
		},
	});

	return {
		titlingData,
		isLoading,
		isPaginating: isFetching || hasNextPage,
		...otherResults,
	};
}
